import React, { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { GoogleMaps } from '../../components';
import {
  defaultLiveVehiclePosition,
  overviewPathData,
} from '../../utilities/dummyData';
import { gpsRotationMarker } from '../../utilities/markersData';
import AlertCard from '../../components/AlertCard/AlertCard';
import { Flex, Typography, Row, Col, Spin } from 'antd';
import OverviewCards from './OverviewCards';
import { API } from '../../services';
import { ErrorMessageContext } from '../../context/ErrorMessageContext';
import { cardsData as initialCardsData } from '../../utilities/SampleAlertCardsData';
import './Overview.css';
import {
  formatDateTime,
  getErrorMessage,
} from '../../utilities/commonFunctions';
import sumBy from 'lodash/sumBy';
import countBy from 'lodash/countBy';
import flatMap from 'lodash/flatMap';
import { DARK, DMS_TYPE } from '../../utilities/constants';
import { ReactComponent as cancel } from '../../assets/icons/cancel.svg';
import { ReactComponent as cancelBlack } from '../../assets/icons/cancel-black.svg';
import Icon from '@ant-design/icons';
import { useNotification } from '../../context/NotificationContext';
import useApiPolling from './../../hooks/useApiPolling';
import { useTheme } from '../../context/ThemeContext';
import AddressFetcher from '../VehicleMonitoring/components/AddressFetcher';

const { Title, Text } = Typography;

const Overview = () => {
  const { addErrorMessage, removeErrorMessage } =
    useContext(ErrorMessageContext);
  const { theme } = useTheme();
  const [markers, setMarkers] = useState([]);
  const [polyLines, setPolylines] = useState([]);
  const [center] = useState(defaultLiveVehiclePosition);
  const [cardsData, setCardsData] = useState(initialCardsData);
  const [sosLoading, setSosLoading] = useState(true);
  const [anomalyLoading, setAnomalyLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [lastUpdatedTime, setLastUpdatedTime] = useState(Date.now());
  const [infoWindow, setInfoWindow] = useState({});
  const [selectedTripFilter, setSelectedTripFilter] = useState(null); // New state for filter
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null); // New state for filter
  const { isFirstLoginSession, cancelOverviewNotification } = useNotification();
  const isFirstSosListCall = useRef(true);
  const isFirstAnomlayListCall = useRef(true);
  const isFirstActiveVehiclesMonitoringCall = useRef(true);

  const handleMarkerClick = (key, position, content, title, children) => {
    setInfoWindow({ title, content, position, children, key, isVisible: true });
  };

  const filteredMarkers = useMemo(() => {
    if (!selectedTripFilter) {
      return markers;
    }
    const selectedMarker = markers?.find(
      marker => marker?.cab_registration === selectedTripFilter
    );
    setHoveredMarkerId(selectedMarker?.key);
    handleMarkerClick(
      `vehicle-info-window-${selectedMarker?.trip_id}`,
      {
        lat: Number(selectedMarker?.position?.lat),
        lng: Number(selectedMarker?.position?.lng),
      },
      {},
      {
        text: `Trip ID: ${selectedMarker?.trip_id}`,
        bgColor: '#00A4FD',
      },
      <>
        <Text style={{ fontSize: '0.75rem', color: '#000000' }}>
          <b> {selectedMarker?.cab_registration}</b>
        </Text>
        <AddressFetcher
          type="map"
          location={{
            latitude: selectedMarker?.position?.lat,
            longitude: selectedMarker?.position?.lng,
          }}
          fontSize="0.75rem"
          title="Location"
        />
      </>
    );
    return markers.filter(marker =>
      marker?.cab_registration?.includes(selectedTripFilter)
    );
  }, [markers, selectedTripFilter]);

  const filteredPolylines = useMemo(() => {
    if (!selectedTripFilter) return polyLines;
    return polyLines.filter(line =>
      line?.cab_registration?.includes(selectedTripFilter)
    );
  }, [polyLines, selectedTripFilter]);

  const updateLastUpdatedTime = time => {
    setLastUpdatedTime(time);
  };

  const transformData = data => {
    const transformedData = { ...data };

    transformedData.driver_drowsiness = transformedData?.major
      ?.filter(item => item?.type === DMS_TYPE.DROWSINESS)
      ?.map(item => ({
        ...item,
        type:
          item?.model_output?.revised_anomaly_type_1 === 'dms_drowsy'
            ? 'dms_drowsy'
            : 'non_drowsy',
      }));

    transformedData.major = transformedData?.major?.filter(
      item =>
        item?.type !== DMS_TYPE.DROWSINESS &&
        item?.type !== DMS_TYPE.DISTRACTION
    );

    return transformedData;
  };

  const updateCardIncidents = (card, typeCount) =>
    card.incidents.map(incident => ({
      ...incident,
      count: typeCount[incident?.key] || 0,
    }));

  const updateCardCount = (anomalyType, card, data) =>
    anomalyType === 'driver_drowsiness'
      ? data[anomalyType]?.length
      : sumBy(card.incidents, 'count');

  const getAnomalyData = async () => {
    setAnomalyLoading(isFirstAnomlayListCall.current && true);
    try {
      const response = await API.getAnomalyColumnData(false);
      const data = transformData(response?.data);

      setCardsData(prev =>
        prev.map(card => {
          if (Object.keys(data).includes(card.key)) {
            const typeCount = countBy(data[card.key], 'type');
            const updatedIncidents = updateCardIncidents(card, typeCount);
            const updatedCount = updateCardCount(
              card.key,
              { ...card, incidents: updatedIncidents },
              data
            );
            return {
              ...card,
              incidents: updatedIncidents,
              count: updatedCount,
            };
          }
          return { ...card };
        })
      );
      updateLastUpdatedTime(Date.now());
    } catch (error) {
      if (isFirstAnomlayListCall.current) {
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch Anomaly details! Please try again after sometime.'
          )
        );
      }
    } finally {
      isFirstAnomlayListCall.current = false;
      setAnomalyLoading(false);
    }
  };

  const updateSOSCard = (card, sosCount, activationTypeCounts) => ({
    ...card,
    count: sosCount,
    incidents: card.incidents.map(incident => ({
      ...incident,
      count: activationTypeCounts[incident.key] || 0,
    })),
  });

  const getSOSData = async () => {
    setSosLoading(isFirstSosListCall.current && true);
    try {
      const response = await API.getSOSColumnData(false);
      if (response?.aborted) {
        return;
      }

      const { data } = response;
      const sosCount = sumBy(Object.values(data), arr => arr.length);
      const activationTypeCounts = countBy(
        flatMap(Object.values(data)),
        'activationtype'
      );
      setCardsData(prevCardsData =>
        prevCardsData.map(card =>
          card.key === 'sos'
            ? updateSOSCard(card, sosCount, activationTypeCounts)
            : card
        )
      );
      updateLastUpdatedTime(Date.now());
    } catch (error) {
      if (isFirstSosListCall.current) {
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch SOS details! Please try again after sometime.'
          )
        );
      }
    } finally {
      isFirstSosListCall.current = false;
      setSosLoading(false);
    }
  };

  const updateMarkers = (prevMarkers, updatedMarker) => {
    const index = prevMarkers.findIndex(
      marker => marker?.cab_registration === updatedMarker?.cab_registration
    );
    if (index !== -1) {
      const newMarkers = [...prevMarkers];
      newMarkers[index] = { ...newMarkers[index], ...updatedMarker };
      return newMarkers;
    }
    return [...prevMarkers, updatedMarker];
  };

  const updatePolylines = (prevLines, updatedMarker) => {
    const index = prevLines.findIndex(
      line => line?.cab_registration === updatedMarker?.cab_registration
    );
    if (index !== -1) {
      const newLines = [...prevLines];
      newLines[index] = { ...newLines[index], ...updatedMarker?.path };
      return newLines;
    }
    return [...prevLines, updatedMarker?.path];
  };

  const handleActiveTripsGpsData = async () => {
    try {
      const { data } = await API.getVehicleOverviewData();
      data?.forEach(trip => {
        if (trip?.gps_list?.length > 0) {
          const updatedMarker = convertGPSDataToMarkers(trip);
          setMarkers(prevMarkers => updateMarkers(prevMarkers, updatedMarker));
          setPolylines(prevLines => updatePolylines(prevLines, updatedMarker));
        }
      });
      updateLastUpdatedTime(Date.now());
    } catch (error) {
      if (isFirstActiveVehiclesMonitoringCall.current) {
        addErrorMessage(
          getErrorMessage(
            error,
            'Unable to fetch active vehicles details! Please try again after sometime.'
          )
        );
      }
    } finally {
      isFirstActiveVehiclesMonitoringCall.current = false;
    }
  };

  useApiPolling(getSOSData);
  useApiPolling(getAnomalyData);
  useApiPolling(() => {
    setOverviewLoading(true);
    handleActiveTripsGpsData().finally(() => setOverviewLoading(false));
  });

  const convertGPSDataToMarkers = data => {
    const path =
      data?.gps_list?.map(point => ({
        lat: Number(point.latitude),
        lng: Number(point.longitude),
      })) || [];
    return {
      ...gpsRotationMarker,
      position: {
        lat: Number(data?.gps_list[0]?.latitude),
        lng: Number(data?.gps_list[0]?.longitude),
      },
      heading: Number(data?.gps_list[0]?.ground_heading) || 0,
      key: `live-location-${data?.trip_id}`,
      cab_registration: data?.cab_registration,
      trip_id: data?.trip_id,
      path: {
        ...overviewPathData,
        path,
        key: `path-${data?.trip_id}`,
        cab_registration: data?.cab_registration,
      },
      onMarkerClick: () =>
        handleMarkerClick(
          `vehicle-info-window-${data?.trip_id}`,
          {
            lat: Number(data?.gps_list[0]?.latitude),
            lng: Number(data?.gps_list[0]?.longitude),
          },
          {},
          {
            text: `Trip ID: ${data?.trip_id}`,
            bgColor: '#00A4FD',
          },
          <>
            <Text style={{ fontSize: '0.75rem', color: '#000000' }}>
              <b> {data?.cab_registration}</b>
            </Text>
            <AddressFetcher
              type="map"
              location={{
                latitude: data?.gps_list[0]?.latitude,
                longitude: data?.gps_list[0]?.longitude,
              }}
              fontSize="0.75rem"
              title="Location"
            />
          </>
        ),
    };
  };

  const handleFilterChange = value => {
    setSelectedTripFilter(value);
    setInfoWindow({ isVisible: false });
    setHoveredMarkerId(null);
  };

  const addNotification = () => {
    if (isFirstLoginSession?.overview) {
      addErrorMessage(
        <Flex gap={16} align="center">
          <Text>The data shown here is of the last 24 hours.</Text>
          <Icon
            component={theme === DARK ? cancel : cancelBlack}
            onClick={() => {
              removeErrorMessage('overview-notification');
              cancelOverviewNotification();
            }}
          />
        </Flex>,
        'notify',
        'overview-notification'
      );
    }
  };

  useEffect(() => {
    addNotification();
    return () => {
      removeErrorMessage('overview-notification');
    };
  }, [theme]);

  return (
    <Flex vertical>
      <Flex gap={5} align="center" justify="end">
        <Text className="last-updated-time">
          {formatDateTime(lastUpdatedTime)}
        </Text>
      </Flex>
      <Flex vertical gap={32}>
        <Flex vertical>
          <Title level={4} style={{ marginBottom: 16 }}>
            Anomalies
          </Title>
          <Spin spinning={anomalyLoading || sosLoading} size="large">
            <Row gutter={[16, 16]}>
              {cardsData.map(card => (
                <Col xs={12} lg={6} key={card?.key}>
                  <AlertCard
                    key={card?.key}
                    alertColor={card?.alertColor}
                    headerIcon={card?.headerIcon}
                    title={card?.title}
                    incidents={card?.incidents}
                    count={card?.count}
                    headerIconHover={card?.headerIconHover}
                  />
                </Col>
              ))}
            </Row>
          </Spin>
        </Flex>
        <Flex vertical>
          <Title level={4} style={{ marginBottom: 16 }}>
            Overview
          </Title>
          <OverviewCards />
        </Flex>
        <Flex vertical>
          <Title level={4} style={{ marginBottom: 16 }}>
            Vehicle Monitoring Overview
          </Title>
          <Spin spinning={overviewLoading} size="large">
            <div className="overview-div-maps">
              <GoogleMaps
                markers={filteredMarkers}
                center={center}
                polylines={filteredPolylines}
                showMarkerHoverEffect={true}
                infoWindow={infoWindow}
                closeInfoWindow={() =>
                  setInfoWindow({
                    isVisible: false,
                  })
                }
                showFilter={true}
                mapFilterProps={{
                  mapFilterList: markers?.map(
                    marker => marker?.cab_registration
                  ),
                  title: 'Active vehicles',
                  onChange: handleFilterChange,
                }}
                hoveredMarker={hoveredMarkerId}
                enableRecenter={true}
              />
            </div>
          </Spin>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default React.memo(Overview);
